/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN SESSION MASTER API
 * Role: Admin
 * Scope: Tenant-based Session Master Management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */

const ADMIN_SESSION_MASTER_ENDPOINT =
  "/admin/sessions/session_master.php";

/* =============================================================
   GET – Fetch Session Masters
   Supports: pagination, search
============================================================= */

export const getAdminSessionMasters = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_SESSION_MASTER_ENDPOINT,
      null,
      "Admin - Fetch Session Masters",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message || "Failed to fetch session masters",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error(
      "Admin Fetch Session Masters Error:",
      error
    );

    return {
      success: false,
      message:
        error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Session Master
============================================================= */

export const getAdminSessionMasterById = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Master ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_SESSION_MASTER_ENDPOINT}?id=${id}`,
      null,
      "Admin - Fetch Session Master By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to fetch session master",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error(
      "Admin Fetch Session Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create Session Master
============================================================= */

export const createAdminSessionMaster = async (
  payload,
  options = {}
) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_SESSION_MASTER_ENDPOINT,
      payload,
      "Admin - Create Session Master",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to create session master",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message:
        res.message ||
        "Session Master created successfully",
    };
  } catch (error) {
    console.error(
      "Admin Create Session Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Session Master
============================================================= */

export const updateAdminSessionMaster = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Master ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_SESSION_MASTER_ENDPOINT}?id=${id}`,
      payload,
      "Admin - Update Session Master",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to update session master",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message:
        res.message ||
        "Session Master updated successfully",
    };
  } catch (error) {
    console.error(
      "Admin Update Session Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Delete Session Master
============================================================= */

export const deleteAdminSessionMaster = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Master ID",
      };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_SESSION_MASTER_ENDPOINT}?id=${id}`,
      null,
      "Admin - Delete Session Master",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to delete session master",
      };
    }

    return {
      success: true,
      message:
        res.message ||
        "Session Master deleted successfully",
    };
  } catch (error) {
    console.error(
      "Admin Delete Session Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};